/* eslint-disable react/jsx-no-target-blank */

import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'fronton-react';
import { useSelector } from 'react-redux';
import { AtIcon, EnvelopeSimpleIcon, PhoneIcon, HandshakeIcon } from '@fronton/icons-react';
import { getIsPickupPoint, getStoreDetail } from 'features/store/ducks/get-store-by-id';
import { getTitle } from 'features/page-model/ducks/get-model';
import { analyticsUtils, patchUrlByLocale } from 'utils';
import { VkLogoIcon } from './vk-logo-icon';
import {
  businessHeader,
  contactsColumn,
  contactsHeader,
  contactsRow,
  contactsTabletDiv,
  iconAlign,
  silentLink,
  storeContactsContainer,
} from './styles';

const getVKTitle = (vkLink = '') => {
  const match = vkLink.match(/\/vk.com\/(.*)/);
  return match && match.length > 1 ? match[1] : '';
};

export const StoreContacts: FC = () => {
  const { t } = useTranslation();
  const { vkLink, b2bEmail, b2cPhone } = useSelector(getStoreDetail) || {};
  const vkTitle = getVKTitle(vkLink);
  const title = useSelector(getTitle);
  const isPickPoint = useSelector(getIsPickupPoint);
  const contacts = t('contacts');
  const becomeCustomer = t('becomeCustomer');
  const writeToSupport = t('writeToSupport');

  const sendClickGA = useCallback(
    (buttonText: string) => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'shopPage', // блок, в котором расположен элемент
        elementName: `${title} / ${contacts}`, // название блока
        elementText: buttonText, // текст кнопки (Как добраться транспортом, Товары в магазине, План магазина, Фото магазина)
      });
    },
    [title, contacts],
  );

  return (
    <div className={storeContactsContainer} data-qa="shop-contacts">
      <div className={contactsColumn}>
        <div className={contactsTabletDiv}>
          <Typography color="text-primary" variant="h3" className={contactsHeader}>
            {contacts}
          </Typography>
          {b2cPhone && (
            <div className={contactsRow}>
              <a
                className={silentLink}
                href={`tel:${b2cPhone}`}
                data-qa="b2cPhone"
                onClick={() => sendClickGA(b2cPhone)}
              >
                <PhoneIcon size="l" className={iconAlign} />
                <Typography variant="m" size="body_long">
                  {b2cPhone}
                </Typography>
              </a>
            </div>
          )}
          {!isPickPoint && (
            <div className={contactsRow}>
              <a
                data-qa="b2cEmail"
                rel="noopener"
                className={silentLink}
                href={patchUrlByLocale('/company/obratnaya-svyaz/')}
                target="_blank"
                onClick={() => sendClickGA(writeToSupport)}
              >
                <EnvelopeSimpleIcon size="l" className={iconAlign} />
                <Typography variant="m" size="body_long">
                  {writeToSupport}
                </Typography>
              </a>
            </div>
          )}
          {vkTitle && vkLink && !isPickPoint && (
            <div className={contactsRow}>
              <a
                rel="noopener"
                data-qa="vkLink"
                className={silentLink}
                href={patchUrlByLocale(vkLink)}
                target="_blank"
                onClick={() => sendClickGA(vkTitle)}
              >
                <VkLogoIcon />
                <Typography variant="m" size="body_long">
                  {vkTitle}
                </Typography>
              </a>
            </div>
          )}
        </div>
        {b2bEmail && !isPickPoint && (
          <div className={contactsTabletDiv}>
            <Typography variant="minor" size="overline" color="text-secondary" className={businessHeader}>
              {t('businessClients')}
            </Typography>
            <div className={contactsRow}>
              <a
                className={silentLink}
                href={`mailto:${b2bEmail}`}
                onClick={() => sendClickGA(b2bEmail)}
                data-qa="b2bEmail"
              >
                <AtIcon size="l" className={iconAlign} />
                <Typography variant="m" size="body_long">
                  {b2bEmail}
                </Typography>
              </a>
            </div>
            <div className={contactsRow}>
              <a
                className={silentLink}
                rel="noopener"
                href={patchUrlByLocale('/biznes/')}
                data-qa="biznes-link"
                target="_blank"
                onClick={() => sendClickGA(becomeCustomer)}
              >
                <HandshakeIcon size="l" className={iconAlign} />
                <Typography variant="m" size="body_long">
                  {becomeCustomer}
                </Typography>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
